import React, { useId, useState } from "react";
import { ArrowUp, BookOpen, ChevronDown, CornerDownRight, RotateCcw } from "lucide-react";
import "./ResearchCompanion.css";

const conversations = [
  {
    id: "automation",
    label: "Automation",
    project: "Antibody screen",
    continuity: "Picking up an interrupted run",
    question: "The antibody screen stopped. Can we restart?",
    answer: "Not yet. The method changed, and the last completed transfer is unclear. I’d reconcile the run log with the plate state first.",
    records: [
      { label: "Method history", status: "Known", detail: "A revised method was loaded after the prior run." },
      { label: "Run log", status: "Missing", detail: "The final completed transfer and current plate state are not reconciled." },
      { label: "Assay controls", status: "Pending", detail: "Control responses have not been reviewed." },
    ],
    followup: "What would make a restart reviewable?",
    reply: "A confirmed checkpoint, matching method version and plate state, and an operator-approved recovery plan. Then review the assay controls before accepting the run.",
  },
  {
    id: "genomics",
    label: "Genomics",
    project: "Target review",
    continuity: "From a locus to a hypothesis",
    question: "This GWAS hit proves our target, right?",
    answer: "It supports a locus–trait association. The effector gene, relevant tissue and mechanism still need evidence.",
    records: [
      { label: "Association", status: "Known", detail: "A locus–trait association is supplied; no gene assignment is established." },
      { label: "Molecular evidence", status: "Missing", detail: "Fine-mapping and colocalization with molecular traits in relevant tissues have not been reviewed." },
      { label: "Orthogonal confirmation", status: "Missing", detail: "Independent functional evidence for the gene and proposed mechanism is not supplied." },
      { label: "Pilot output", status: "Proposed", detail: "A source-linked evidence table, competing gene and tissue hypotheses, unresolved gaps, and one proposed validation experiment." },
    ],
    source: { label: "Locus-to-gene methods · Nature Genetics", href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC7611956/" },
    followup: "What would strengthen the target hypothesis?",
    reply: "Review fine-mapping and tissue-relevant molecular evidence, then test the proposed gene–mechanism link with an independent perturbation and readout. Keep conflicting evidence in view.",
  },
  {
    id: "scientific-ai",
    label: "Scientific AI",
    project: "Agent evaluation",
    continuity: "Checking the decision behind the output",
    question: "The agent made a plot. Did it solve the task?",
    answer: "A plot shows execution, not correctness. The example trace leaves a required sample exclusion unchecked, so I’d keep the result under review.",
    records: [
      { label: "Comparison plan", status: "Proposed", detail: "Test the baseline and specialist on the same held-out cases, inputs, model and resource budget." },
      { label: "Trace", status: "Known", detail: "An output was saved; the required sample exclusion was not checked." },
      { label: "Expert review", status: "Missing", detail: "No adjudicated answer or repeat-run comparison is supplied." },
    ],
    followup: "How would we test the specialist fairly?",
    reply: "Change only the workflow being tested. Keep the model, cases, inputs and budget matched; save traces and have reviewers judge the required decisions without seeing the system label.",
  },
  {
    id: "disease-biology",
    label: "Disease biology",
    project: "Delivery study",
    continuity: "Working through conflicting readouts",
    question: "Uptake rose, but knockdown didn’t. Is the target wrong?",
    answer: "That mismatch doesn’t settle the target hypothesis. Uptake and knockdown answer different questions; I’d inspect the paired readouts and controls.",
    records: [
      { label: "Readout summary", status: "Reported", detail: "Uptake is higher; knockdown is unchanged in this example." },
      { label: "Run pairing", status: "Missing", detail: "Matched sample IDs, timing and raw readouts are not supplied." },
      { label: "Assay controls", status: "Pending", detail: "Control responses and an independent knockdown measurement remain to be reviewed." },
    ],
    source: { label: "Delivery and knockdown · Nature Communications", href: "https://www.nature.com/articles/s41467-023-36752-1" },
    followup: "What should we compare first?",
    reply: "First match the samples, timing and controls. If the mismatch holds, compare delivery and knockdown evidence before choosing a follow-up experiment; don’t assign a cause yet.",
  },
];

function AssistantMessage({ children }) {
  return (
    <div className="companion-message companion-message-assistant">
      <img src="/favico.svg" alt="Cornucopia" width="28" height="32" />
      <div>{children}</div>
    </div>
  );
}

export default function ResearchCompanion() {
  const [selected, setSelected] = useState(0);
  const [continued, setContinued] = useState(false);
  const id = useId();
  const conversation = conversations[selected];
  const replyId = `${id}-${conversation.id}-reply`;

  const choose = (index) => {
    setSelected(index);
    setContinued(false);
  };

  return (
    <figure className="research-companion" aria-label="Example conversation with Cornucopia">
      <div className="companion-window">
        <div className="companion-header">
          <img className="companion-brand-light" src="/Logo.svg" alt="Cornucopia" width="152" height="42" />
          <img className="companion-brand-dark" src="/Logo-dark.svg" alt="Cornucopia" width="152" height="42" />
          <div className="companion-project"><span>{conversation.project}</span><span className="companion-example-label">Example</span></div>
        </div>

        <div className="companion-thread" key={selected} role="log" aria-label="Example research conversation" aria-live="polite" aria-relevant="additions text">
          <div className="companion-message companion-message-you">
            <p><span className="sr-only">You: </span>{conversation.question}</p>
            <img src="/images/discovery/principal-scientist.png" alt="" width="602" height="435" />
          </div>
          <AssistantMessage>
            <p>{conversation.answer}</p>
            <details className="companion-note">
              <summary><BookOpen size={13} aria-hidden="true" /> Inspect example records <ChevronDown size={13} aria-hidden="true" /></summary>
              <div className="companion-records">
                <p className="companion-records-caption">Illustrative records for this conversation.</p>
                <dl>
                  {conversation.records.map(({ label, status, detail }) => (
                    <div className="companion-record" key={label}>
                      <dt>{label}<span>{status}</span></dt>
                      <dd>{detail}</dd>
                    </div>
                  ))}
                </dl>
                {conversation.source && <a className="companion-source" href={conversation.source.href} target="_blank" rel="noreferrer">{conversation.source.label}<span className="sr-only"> (opens in a new tab)</span></a>}
              </div>
            </details>
          </AssistantMessage>
          {continued && <div className="companion-followup" id={replyId}>
            <div className="companion-message companion-message-you">
              <p><span className="sr-only">You: </span>{conversation.followup}</p>
            </div>
            <AssistantMessage><p>{conversation.reply}</p></AssistantMessage>
          </div>}
          <button className="companion-continue" type="button" onClick={() => setContinued(!continued)} aria-expanded={continued} aria-controls={replyId}>
            {continued ? <RotateCcw size={14} aria-hidden="true" /> : <CornerDownRight size={14} aria-hidden="true" />}
            <span>{continued ? "Back to the first question" : conversation.followup}</span>
            {!continued && <ArrowUp size={16} className="companion-send" aria-hidden="true" />}
          </button>
        </div>

        <div className="companion-examples" role="group" aria-label="Choose an example workflow">
          {conversations.map(({ label }, index) => <button key={label} type="button" aria-pressed={selected === index} onClick={() => choose(index)}>{label}</button>)}
        </div>
      </div>
    </figure>
  );
}
